import React, { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import {
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons'
import { IconButton, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import SEARCH_JOB_EXECUTIONS from 'Apollo/Queries/SearchJobExecutions'
import GET_JOB_STATS from 'Apollo/Queries/GetJobStats'
import {
  AdminPageWrapper,
  AdminTable,
  SearchFilters,
  AdminTableColumn,
} from '../AdminComponents'
import { JobSearchBar } from './JobSearchBar'
import {
  StatsContainer,
  StatsWrapper,
  StatsLabel,
  JobStatsWrapper,
  JobStatItem,
  JobStatName,
  JobStatChips,
  StatChip,
  BackButton,
  ActionsContainer,
  StatusChip,
  ErrorTooltip,
} from './JobAdmin.styled'

// Local interface for Job Admin search filters
interface JobSearchFilters {
  id?: string
  jobName?: string
  status?: string
  hoursBack?: string
}

dayjs.extend(relativeTime)

interface JobExecution {
  id: string
  jobName: string
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED'
  payload: Record<string, unknown> | null
  result: Record<string, unknown> | null
  errorMessage: string | null
  errorStack: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  duration: number | null
  createdByUserId: number | null
  organizationId: number | null
  createdByUser: {
    id: number
    firstName: string
    lastName: string
  } | null
}

interface JobStat {
  jobName: string
  total: number
  pending: number
  running: number
  completed: number
  failed: number
  successRate: number
}

export const JobAdmin: React.FC = () => {
  const history = useHistory()

  // Search and filter state
  const [searchFilters, setSearchFilters] = useState<JobSearchFilters>({})
  const [page, setPage] = useState(0)
  const [pageSize] = useState(20)
  const [orderBy] = useState('createdAt')
  const [orderDirection] = useState<'asc' | 'desc'>('desc')

  // Build query variables
  const queryVariables = useMemo(() => {
    const filter: Record<string, unknown> = {}

    if (searchFilters.id) {
      filter.id = searchFilters.id
    }
    if (searchFilters.jobName) {
      filter.jobName = searchFilters.jobName
    }
    if (searchFilters.status) {
      filter.status = searchFilters.status
    }
    if (searchFilters.hoursBack) {
      const hoursBack = parseInt(searchFilters.hoursBack, 10)
      const createdAfter = new Date()
      createdAfter.setHours(createdAfter.getHours() - hoursBack)
      filter.createdAfter = createdAfter.toISOString()
    }

    return {
      filter,
      limit: pageSize,
      offset: page * pageSize,
      orderBy,
      orderDirection,
    }
  }, [searchFilters, page, pageSize, orderBy, orderDirection])

  // Fetch job executions
  const { data, loading, error, refetch } = useQuery<{
    searchJobExecutions: {
      nodes: JobExecution[]
      totalCount: number
      hasNextPage: boolean
      hasPreviousPage: boolean
    }
  }>(SEARCH_JOB_EXECUTIONS, {
    variables: queryVariables,
    pollInterval: 5000, // Poll every 5 seconds for updates
  })

  // Fetch job stats
  const { data: statsData } = useQuery<{
    jobStats: {
      jobStats: JobStat[]
    }
  }>(GET_JOB_STATS, {
    variables: { hoursBack: parseInt(searchFilters.hoursBack || '24', 10) },
  })

  const searchFields = [
    {
      key: 'id',
      label: 'Job ID',
      placeholder: 'Enter job ID',
    },
    {
      key: 'jobName',
      label: 'Job Name',
      placeholder: 'Enter job name',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'PENDING', label: 'Pending' },
        { value: 'RUNNING', label: 'Running' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'FAILED', label: 'Failed' },
      ],
    },
    {
      key: 'hoursBack',
      label: 'Hours Back',
      placeholder: 'e.g., 24',
      type: 'number' as const,
    },
  ]

  const getStatusColor = (
    status: string
  ): 'default' | 'info' | 'success' | 'error' => {
    switch (status) {
      case 'PENDING':
        return 'default'
      case 'RUNNING':
        return 'info'
      case 'COMPLETED':
        return 'success'
      case 'FAILED':
        return 'error'
      default:
        return 'default'
    }
  }

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-'
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`
    return `${(ms / 3600000).toFixed(1)}h`
  }

  const columns: AdminTableColumn<JobExecution>[] = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => value.substring(0, 8),
    },
    {
      key: 'jobName',
      label: 'Job Name',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <StatusChip
          label={value}
          size='small'
          color={getStatusColor(value)}
          status={value}
        />
      ),
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (value) => dayjs(value).fromNow(),
      sortable: true,
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (value) => formatDuration(value),
    },
    {
      key: 'createdByUser',
      label: 'Created By',
      render: (_, job) =>
        job.createdByUser
          ? `${job.createdByUser.firstName} ${job.createdByUser.lastName}`
          : 'System',
    },
    {
      key: 'errorMessage',
      label: 'Error',
      render: (value) => {
        if (!value) return '-'
        const truncated =
          value.length > 50 ? `${value.substring(0, 50)}...` : value
        return (
          <Tooltip title={value}>
            <ErrorTooltip>{truncated}</ErrorTooltip>
          </Tooltip>
        )
      },
    },
  ]

  const jobExecutions = useMemo(
    () => data?.searchJobExecutions?.nodes || [],
    [data?.searchJobExecutions]
  )

  const totalCount = data?.searchJobExecutions?.totalCount || 0

  const handleRowClick = (job: JobExecution) => {
    history.push(`/internal/self-serve/job-admin/${job.id}`)
  }

  const handleSearch = React.useCallback((filters: JobSearchFilters) => {
    setSearchFilters(filters)
    setPage(0) // Reset to first page when search filters change
  }, [])

  if (error) {
    return (
      <AdminPageWrapper title='Job Dashboard'>
        <div>Error loading jobs: {error.message}</div>
      </AdminPageWrapper>
    )
  }

  return (
    <AdminPageWrapper
      title='Job Dashboard'
      subtitle='Monitor and manage background jobs'
      actions={
        <ActionsContainer>
          <BackButton
            variant='contained'
            size='small'
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/internal/self-serve')}
          >
            Back to Self Serve
          </BackButton>
          <IconButton
            onClick={() => refetch()}
            size='small'
            style={{ marginLeft: 'auto' }}
          >
            <RefreshIcon />
          </IconButton>
        </ActionsContainer>
      }
    >
      <JobSearchBar
        fields={searchFields}
        onSearch={handleSearch}
        initialFilters={searchFilters as SearchFilters}
      />

      {statsData && statsData.jobStats.jobStats.length > 0 && (
        <StatsContainer>
          <StatsWrapper>
            <StatsLabel variant='body2'>
              Job Stats ({searchFilters.hoursBack || '24'}h):
            </StatsLabel>
            <JobStatsWrapper>
              {statsData.jobStats.jobStats.map((stat, index) => (
                <JobStatItem
                  key={stat.jobName}
                  isLast={index === statsData.jobStats.jobStats.length - 1}
                >
                  <JobStatName variant='body2'>{stat.jobName}:</JobStatName>
                  <JobStatChips>
                    <StatChip
                      size='small'
                      label={`${stat.total} total`}
                      variant='outlined'
                    />
                    {stat.running > 0 && (
                      <StatChip
                        size='small'
                        label={`${stat.running} running`}
                        color='info'
                      />
                    )}
                    {stat.failed > 0 && (
                      <StatChip
                        size='small'
                        label={`${stat.failed} failed`}
                        color='error'
                      />
                    )}
                    <StatChip
                      size='small'
                      label={`${stat.successRate.toFixed(0)}% success`}
                      color={stat.successRate >= 90 ? 'success' : 'warning'}
                      variant={stat.successRate >= 90 ? 'filled' : 'filled'}
                    />
                  </JobStatChips>
                </JobStatItem>
              ))}
            </JobStatsWrapper>
          </StatsWrapper>
        </StatsContainer>
      )}

      <AdminTable<JobExecution>
        columns={columns}
        data={jobExecutions}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage='No jobs found'
        pageSize={pageSize}
        totalCount={totalCount}
        currentPage={page}
        onPageChange={setPage}
        serverSidePagination
        actions={[
          {
            icon: <ViewIcon />,
            tooltip: 'View details',
            onClick: handleRowClick,
          },
        ]}
      />
    </AdminPageWrapper>
  )
}
